/**
 * SecureShareDialog
 * =================
 * TREATX-SECURE-MEDICAL-FILE-SHARING-10B
 *
 * Reusable deliberate-consent dialog for sharing medical files with a clinic.
 * Enforces the **Upload ≠ Share** rule: the dialog is the ONLY path that
 * creates an `attachment_access` grant. It must be triggered by an explicit
 * patient action (post-upload prompt in PatientMessagesV2 or per-file
 * "Share" button in MedicalFilesPage). Never auto-open.
 *
 * UX guarantees:
 *  - Recipient (clinic name) is auto-resolved or selected from a list of
 *    the patient's existing clinic conversations. Never expose clinic_id,
 *    provider_id, thread_id, booking_id, or grant_id to the patient.
 *  - Purpose dropdown is mandatory before "Share" is enabled.
 *  - Consent text is rendered verbatim and the consent checkbox is the
 *    final gate.
 *  - On confirm, calls POST /api/patient/attachments/share with
 *    purpose / consent_text_version / surface_used / grant_expires_at.
 *
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - onShared: ({ shared, count }) => void
 *  - files: Array<{ attachment_id, file_name, file_type }>   (selected files)
 *  - surface: "patient_messages" | "medical_vault"
 *  - recipient: { clinic_id, clinic_name, thread_id? } | null
 *      When provided, the recipient picker is hidden and the recipient is
 *      shown as a fixed banner (auto-resolved from current thread).
 *  - recipientOptions: Array<{ clinic_id, clinic_name, thread_id? }>
 *      Used when `recipient` is null (Vault flow). Shown as a picker.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ShieldCheck, X, Building2, FileText, FileImage, Loader2, AlertCircle, UserRound } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';

// TREATX-L1-B — Canonical content_key for the medical-file-sharing
// consent text. The runtime resolves the currently-published version
// and locale via the existing legal-content authority; the frontend
// no longer hard-codes the legal text.
export const CONSENT_CONTENT_KEY = 'medical_file_share_consent_text';

// Legacy free-string identifier kept for backward compatibility with
// consumers that only read `CONSENT_TEXT_VERSION`. New shares also
// send `consent_content_key` + `consent_content_version` so evidence
// is attributable to a specific admin-managed version.
export const CONSENT_TEXT_VERSION = 'medical-share-managed-content-v1';

// Local-only conservative fallback used ONLY when the canonical
// resolver fails and Admin has not yet enabled the runtime override.
// Rendering this fallback still permits share (backend accepts a
// nullable canonical pair for compat), but the UI clearly labels the
// state so patients understand the current copy source.
const FALLBACK_CONSENT_TEXT = (
  'I voluntarily share the selected medical files with this clinic for the ' +
  'purpose I have chosen. Access remains active until I stop sharing it ' +
  'from my Medical Files vault. Secure download links may refresh ' +
  'periodically for safety, but this does not end the access. Every ' +
  'share and revoke action is recorded in an immutable audit trail.'
);

// SEC-MEDICAL-FILE-INTERNAL-AUTHZ-01 / UX-ROLE-LABEL-SALES-REP-01 —
// patient-readable labels for the canonical provider roles that can be
// selected as a recipient. DISPLAY ONLY: the canonical role id
// (`provider_sales_rep`, …) is what is sent as the authorization role.
// Copy lives in the TreatX i18n bundles under
// `patient.medicalFiles.recipientRoles.*` (EN/TR parity).
const ROLE_LABEL_KEY = (role) => `patient.medicalFiles.recipientRoles.${role}`;

const PURPOSE_OPTIONS = [
  { value: 'treatment_evaluation', label: 'Treatment evaluation' },
  { value: 'medical_history', label: 'Medical history / context' },
  { value: 'pre_treatment_review', label: 'Pre-treatment review' },
  { value: 'post_treatment_followup', label: 'Post-treatment follow-up' },
  { value: 'other', label: 'Other' },
];

const FileChip = ({ file }) => {
  const ext = (file.file_type || '').toLowerCase();
  const isImage = ['jpg', 'jpeg', 'png'].includes(ext);
  return (
    <div
      className="flex items-center gap-2 px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs"
      data-testid={`secure-share-file-chip-${file.attachment_id || file.file_name}`}
    >
      {isImage ? (
        <FileImage className="h-3.5 w-3.5 text-blue-500" />
      ) : (
        <FileText className="h-3.5 w-3.5 text-red-500" />
      )}
      <span className="truncate max-w-[180px]">{file.file_name}</span>
    </div>
  );
};

// TREATX-10B-UX-ACCEPTANCE-REMEDIATION-02
// Renders resolved clinics up top. Orphan / unresolved rows sit behind
// a "Show N unavailable clinics" toggle and render disabled — they
// cannot be selected because the patient cannot meaningfully consent
// to sharing with an unidentified party.
const RecipientList = ({ options, picked, onPick }) => {
  const resolved = options.filter(o => o.name_resolved !== false);
  const unresolved = options.filter(o => o.name_resolved === false);
  const [showUnresolved, setShowUnresolved] = useState(false);

  const renderButton = (opt, { disabled = false } = {}) => {
    // UAT-MEDICAL-JOURNEY-SCOPED-AUTHZ-01 — one option per JOURNEY, so two
    // journeys at the same Provider are two distinguishable choices.
    const journeyId = opt.conversation_group_id || opt.thread_id || 'no-journey';
    const key = `${opt.clinic_id}__${journeyId}`;
    const isPicked = !disabled &&
      picked?.clinic_id === opt.clinic_id &&
      (picked?.conversation_group_id || picked?.thread_id || null) ===
        (opt.conversation_group_id || opt.thread_id || null);
    const detail = [opt.treatment, opt.status ? String(opt.status).replace(/_/g, ' ') : '']
      .filter(Boolean).join(' · ');
    return (
      <button
        key={key}
        type="button"
        onClick={() => !disabled && onPick(opt)}
        disabled={disabled}
        className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg border transition ${
          disabled
            ? 'bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed opacity-70'
            : isPicked
              ? 'bg-blue-50 border-blue-300 ring-1 ring-blue-200'
              : 'bg-white border-slate-200 hover:bg-slate-50'
        }`}
        data-testid={`secure-share-recipient-option-${opt.clinic_id}`}
        data-journey-testid={`secure-share-journey-option-${journeyId}`}
      >
        <Building2 className={`h-4 w-4 ${disabled ? 'text-slate-300' : isPicked ? 'text-blue-600' : 'text-slate-400'}`} />
        <span className={`text-sm ${disabled ? 'text-slate-400' : 'text-slate-800'}`}>
          {opt.clinic_name || 'Clinic'}
          {detail && (
            <span className="block text-[11px] text-slate-500 capitalize">{detail}</span>
          )}
        </span>
        {disabled && (
          <span className="ml-auto text-[10px] uppercase tracking-wide text-slate-400">
            Unavailable
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      {resolved.map(o => renderButton(o))}
      {unresolved.length > 0 && (
        <>
          {!showUnresolved ? (
            <button
              type="button"
              onClick={() => setShowUnresolved(true)}
              className="w-full text-xs text-slate-500 hover:text-slate-700 text-left px-3 py-1.5"
              data-testid="secure-share-show-unavailable-toggle"
            >
              Show {unresolved.length} unavailable clinic{unresolved.length === 1 ? '' : 's'}…
            </button>
          ) : (
            <>
              <div className="text-[10px] text-slate-400 uppercase tracking-wide px-3 pt-2 pb-1">
                Unavailable — no clinic name on file
              </div>
              {unresolved.map(o => renderButton(o, { disabled: true }))}
            </>
          )}
        </>
      )}
    </>
  );
};

const SecureShareDialog = ({
  open,
  onClose,
  onShared,
  files = [],
  surface,
  recipient = null,
  recipientOptions = [],
}) => {
  // The parent always conditionally mounts this component when opening
  // a fresh share, so initial-state lazy init keeps the dialog clean
  // without needing a state-reset effect (which would trip the
  // react-hooks/set-state-in-effect rule).
  const [purpose, setPurpose] = useState('');
  const [consented, setConsented] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Vault-flow recipient selection (key = clinic_id || thread_id)
  const [pickedRecipient, setPickedRecipient] = useState(() => recipient || null);
  // SEC-MEDICAL-FILE-INTERNAL-AUTHZ-01 / UAT-MEDICAL-FILE-SHARING-UX-01
  // Person-level sharing. `null` = untouched, so a single eligible person
  // is pre-selected (and always shown before confirmation) while a
  // multi-person clinic starts empty and forces a deliberate choice.
  const [manualSelection, setManualSelection] = useState(null);

  // TREATX-L1-B — resolve the canonical, admin-managed consent text
  // from the legal-content authority. Locale = current i18n language.
  // On failure we surface the state to the user and fall back to the
  // conservative in-file copy so patients are never asked to consent
  // to blank text; but evidence writes still carry a nullable
  // canonical pair so audit rows show the resolution failure.
  const { t, i18n } = useTranslation();
  // i18next returns the key itself when a bundle entry is missing, so fall
  // back to the canonical role id rather than leaking a translation key.
  const roleLabel = (role) => {
    const key = ROLE_LABEL_KEY(role);
    const label = t(key);
    return label && label !== key ? label : role;
  };
  const [managed, setManaged] = useState({
    loading: true,
    body: null,
    version: null,
    contentKey: CONSENT_CONTENT_KEY,
    resolvedLanguage: null,
    isFallback: false,
    resolutionError: null,
  });

  useEffect(() => {
    let cancelled = false;
    const lang = (i18n?.language || 'en').split('-')[0];
    (async () => {
      try {
        const res = await api.get('/public/content/resolve', {
          params: { key: CONSENT_CONTENT_KEY, language: lang },
        });
        if (cancelled) return;
        const payload = res?.data || {};
        setManaged({
          loading: false,
          body: payload.body || null,
          version: payload.version || null,
          contentKey: payload.content_key || CONSENT_CONTENT_KEY,
          resolvedLanguage: payload.language || lang,
          isFallback: Boolean(payload.is_fallback),
          resolutionError: null,
        });
      } catch (err) {
        if (cancelled) return;
        setManaged({
          loading: false,
          body: null,
          version: null,
          contentKey: CONSENT_CONTENT_KEY,
          resolvedLanguage: lang,
          isFallback: false,
          resolutionError:
            err?.response?.data?.detail || err?.message || 'resolve_failed',
        });
      }
    })();
    return () => { cancelled = true; };
  }, [i18n?.language]);

  const activeRecipient = recipient || pickedRecipient;
  const eligiblePeople = useMemo(
    () => (activeRecipient?.people || []),
    [activeRecipient],
  );
  // UAT-MEDICAL-JOURNEY-SCOPED-AUTHZ-01 — selection identity is
  // (user_id, role), never user_id: one human may be Sales AND Coordinator
  // in the same journey and the patient grants ONE relationship at a time.
  const keyOf = (p) => `${p.user_id}::${p.role}`;
  const selectedKeys = useMemo(() => {
    if (manualSelection) return manualSelection;
    return eligiblePeople.length === 1 ? [keyOf(eligiblePeople[0])] : [];
  }, [manualSelection, eligiblePeople]);
  const selectedPeople = useMemo(
    () => eligiblePeople.filter(p => selectedKeys.includes(keyOf(p))),
    [eligiblePeople, selectedKeys],
  );

  const togglePerson = (person) => {
    const k = keyOf(person);
    setManualSelection(
      selectedKeys.includes(k)
        ? selectedKeys.filter(id => id !== k)
        : [...selectedKeys, k],
    );
  };

  const canSubmit = useMemo(() => {
    if (!files.length) return false;
    if (!purpose) return false;
    if (!consented) return false;
    if (!activeRecipient || !activeRecipient.clinic_id) return false;
    // A medical file is never shared with a whole provider.
    if (selectedKeys.length === 0) return false;
    // Every grant is issued inside one exact journey/conversation.
    if (!activeRecipient.thread_id) return false;
    return !submitting;
  }, [files, purpose, consented, activeRecipient, selectedKeys, submitting]);

  const handleShare = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await api.post('/patient/attachments/share', {
        attachment_ids: files.map(f => f.attachment_id).filter(Boolean),
        clinic_ids: [activeRecipient.clinic_id],
        // Role-scoped person-level grants — the ONLY authority that opens
        // the file. The server re-derives every pair from the journey.
        recipients: selectedPeople.map(p => ({ user_id: p.user_id, role: p.role })),
        thread_id: activeRecipient.thread_id,
        purpose,
        // Legacy free-string identifier — kept for callers that only
        // read this field on the audit row.
        consent_text_version: managed.version
          ? `${managed.contentKey}::v${managed.version}::${managed.resolvedLanguage || 'en'}`
          : CONSENT_TEXT_VERSION,
        surface_used: surface,
        // L1-B canonical authority pair. Nullable when the resolver
        // failed (rare — Admin has not yet published the text). The
        // backend accepts nullable for legacy compat but flags such
        // rows in the audit trail.
        consent_content_key: managed.version ? managed.contentKey : null,
        consent_content_version: managed.version || null,
      });
      if (res.data?.success && (res.data.count || 0) > 0) {
        const names = selectedPeople.map(p => p.name).join(', ');
        toast.success('Files shared securely', {
          description: `${files.length} file(s) shared with ${names || activeRecipient.clinic_name || 'the clinic'}.`,
        });
        onShared && onShared({ shared: res.data.shared || [], count: res.data.count || 0 });
        onClose && onClose();
      } else {
        toast.error('Share failed', {
          description: res.data?.detail || res.data?.error || 'Could not share files',
        });
      }
    } catch (err) {
      toast.error('Share failed', {
        description: err?.response?.data?.detail || err?.message || 'Network error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4"
      data-testid="secure-share-dialog"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900" data-testid="secure-share-title">
                Securely share medical files
              </h3>
              <p className="text-[11px] text-slate-500 mt-0.5" data-testid="secure-share-subtitle">
                Your files stay private in your vault until you share them here.
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} data-testid="secure-share-close-btn">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Recipient — auto-resolved or picker */}
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1.5">Sharing with</label>
            {recipient ? (
              <div
                className="flex items-center gap-2.5 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2"
                data-testid="secure-share-recipient-fixed"
              >
                <Building2 className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">
                  {recipient.clinic_name || 'Clinic'}
                </span>
              </div>
            ) : (
              <div className="space-y-1.5" data-testid="secure-share-recipient-picker">
                {recipientOptions.length === 0 ? (
                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                    <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>
                      No active clinic conversation found. Start a conversation with a clinic before sharing files.
                    </span>
                  </div>
                ) : (
                  <RecipientList
                    options={recipientOptions}
                    picked={pickedRecipient}
                    onPick={(opt) => { setPickedRecipient(opt); setManualSelection(null); }}
                  />
                )}
              </div>
            )}
          </div>

          {/* SEC-MEDICAL-FILE-INTERNAL-AUTHZ-01 — person-level recipients.
              Conversation access != medical file access, so the patient
              always sees exactly who will be able to open the file. Only
              people with an existing relationship are listed. */}
          {activeRecipient?.clinic_id && (
            <div data-testid="secure-share-people-section">
              <label className="text-xs font-medium text-slate-700 block mb-1.5">
                Who can access this file? <span className="text-red-500">*</span>
              </label>
              {eligiblePeople.length === 0 ? (
                <div
                  className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800"
                  data-testid="secure-share-no-eligible-people"
                >
                  <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>
                    No one at this clinic is currently handling your case, so
                    there is nobody to share with yet. Once a coordinator,
                    consultant or doctor is assigned they will appear here.
                  </span>
                </div>
              ) : (
                <div className="space-y-1.5" data-testid="secure-share-people-list">
                  {eligiblePeople.map(p => {
                    const checked = selectedKeys.includes(`${p.user_id}::${p.role}`);
                    return (
                      <label
                        key={`${p.user_id}::${p.role}`}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition ${
                          checked
                            ? 'bg-emerald-50 border-emerald-300 ring-1 ring-emerald-200'
                            : 'bg-white border-slate-200 hover:bg-slate-50'
                        }`}
                        data-testid={`secure-share-person-${p.user_id}-${p.role}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePerson(p)}
                          className="accent-emerald-600"
                          data-testid={`secure-share-person-checkbox-${p.user_id}-${p.role}`}
                        />
                        <UserRound className={`h-4 w-4 ${checked ? 'text-emerald-600' : 'text-slate-400'}`} />
                        <span className="text-sm text-slate-800">{p.name}</span>
                        <span className="ml-auto text-[10px] uppercase tracking-wide text-slate-400">
                          {roleLabel(p.role)}
                        </span>
                      </label>
                    );
                  })}
                  <p className="text-[11px] text-slate-500 pt-0.5">
                    Only the people you tick will be able to open this file.
                    Everyone else at the clinic stays blocked, even if you talk
                    to them in the same conversation.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Files */}
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1.5">
              {files.length} file{files.length === 1 ? '' : 's'} to share
            </label>
            <div className="flex flex-wrap gap-1.5" data-testid="secure-share-files">
              {files.map(f => (
                <FileChip key={f.attachment_id || f.file_name} file={f} />
              ))}
            </div>
          </div>

          {/* Purpose */}
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1.5">
              Purpose <span className="text-red-500">*</span>
            </label>
            <select
              value={purpose}
              onChange={e => setPurpose(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
              data-testid="secure-share-purpose-select"
            >
              <option value="">Select a purpose…</option>
              {PURPOSE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Access duration explainer — separate from consent so the
              owner-UX requirement (clear access wording) is satisfied
              even if the patient skim-reads the consent block. */}
          <div
            className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2"
            data-testid="secure-share-access-duration"
          >
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
            <p className="text-[11px] text-emerald-900 leading-relaxed">
              <span className="font-semibold">Access stays active until you stop it.</span>{' '}
              You can stop sharing any time from your Medical Files. The clinic
              loses access immediately. Your file stays in your vault.
            </p>
          </div>

          {/* Consent block — text sourced from the canonical
              admin-managed legal-content authority. */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
            {managed.loading ? (
              <p
                className="text-[11px] text-slate-500 italic"
                data-testid="secure-share-consent-loading"
              >
                Loading consent text…
              </p>
            ) : (
              <>
                <p
                  className="text-[11px] text-slate-600 leading-relaxed whitespace-pre-line"
                  data-testid="secure-share-consent-text"
                  data-content-key={managed.contentKey}
                  data-content-version={managed.version ?? ''}
                  data-consent-language={managed.resolvedLanguage ?? ''}
                >
                  {managed.body || FALLBACK_CONSENT_TEXT}
                </p>
                {managed.resolutionError && (
                  <p
                    className="text-[10px] text-amber-700"
                    data-testid="secure-share-consent-fallback-notice"
                  >
                    Displaying default consent copy — the platform&apos;s managed
                    version is temporarily unavailable.
                  </p>
                )}
              </>
            )}
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={consented}
                onChange={e => setConsented(e.target.checked)}
                className="mt-0.5 accent-emerald-600"
                data-testid="secure-share-consent-checkbox"
                disabled={managed.loading}
              />
              <span className="text-xs text-slate-800" data-testid="secure-share-consent-label">
                {selectedPeople.length > 0
                  ? `I have read the above and consent to sharing these files with ${selectedPeople.map(p => `${p.name} (${roleLabel(p.role)})`).join(', ')}.`
                  : 'I have read the above and consent to sharing these files with the people I select.'}
              </span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 bg-slate-50 border-t">
          <Button variant="outline" onClick={onClose} data-testid="secure-share-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={handleShare}
            disabled={!canSubmit}
            className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
            data-testid="secure-share-confirm-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                Sharing…
              </>
            ) : (
              <>
                <ShieldCheck className="h-4 w-4 mr-1.5" />
                Share securely
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SecureShareDialog;
